import { useEffect, useState } from 'react'
import { fetchFunnel, fetchDropoff, type FunnelRow, type DropoffRow } from '../../lib/api'
import { useDateRangeCtx } from '../DateRangeContext'
import { useFlowFilter } from '../FlowFilterContext'
import { DateRangeTabs } from '../DateRangeTabs'
import { FunnelChart } from '../widgets/FunnelChart'
import { DropoffList } from '../widgets/DropoffList'
import { LiveSessions } from '../widgets/LiveSessions'

function ComparisonHeader() {
  return (
    <div className="funnel-compare-header">
      <div className="funnel-compare-col">
        <div className="card-title">Chat</div>
        <div className="muted small">Funnel</div>
      </div>
      <div className="funnel-compare-col">
        <div className="card-title">Calculator</div>
        <div className="muted small">Funnel</div>
      </div>
    </div>
  )
}

export function FunnelSection() {
  const { range } = useDateRangeCtx()
  const { variant, variantParam, audienceParam } = useFlowFilter()
  const [single, setSingle] = useState<FunnelRow[] | null>(null)
  const [singleActive, setSingleActive] = useState(0)
  const [singleResume, setSingleResume] = useState(0)
  const [chatFunnel, setChatFunnel] = useState<FunnelRow[] | null>(null)
  const [chatActive, setChatActive] = useState(0)
  const [chatResume, setChatResume] = useState(0)
  const [calcFunnel, setCalcFunnel] = useState<FunnelRow[] | null>(null)
  const [calcActive, setCalcActive] = useState(0)
  const [calcResume, setCalcResume] = useState(0)
  const [dropoff, setDropoff] = useState<DropoffRow[] | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true); setSingle(null); setChatFunnel(null); setCalcFunnel(null); setDropoff(null)
    setSingleActive(0); setChatActive(0); setCalcActive(0)
    setSingleResume(0); setChatResume(0); setCalcResume(0)

    const tasks: Promise<unknown>[] = []
    if (variant === 'all') {
      tasks.push(
        fetchFunnel(range, 'chat', audienceParam).then((f) => { if (!cancelled) { setChatFunnel(f.rows); setChatActive(f.activeExcluded ?? 0); setChatResume(f.resumeLeads ?? 0) } }),
        fetchFunnel(range, 'calculator', audienceParam).then((f) => { if (!cancelled) { setCalcFunnel(f.rows); setCalcActive(f.activeExcluded ?? 0); setCalcResume(f.resumeLeads ?? 0) } }),
      )
    } else {
      tasks.push(fetchFunnel(range, variant, audienceParam).then((f) => { if (!cancelled) { setSingle(f.rows); setSingleActive(f.activeExcluded ?? 0); setSingleResume(f.resumeLeads ?? 0) } }))
    }
    tasks.push(fetchDropoff(range, variantParam, audienceParam).then((d) => !cancelled && setDropoff(d.rows)))

    Promise.all(tasks)
      .catch((err) => console.error('[funnel]', err))
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [range.from, range.to, variant, variantParam, audienceParam])

  return (
    <>
      <div className="section-toolbar">
        <DateRangeTabs />
      </div>

      <LiveSessions />

      {variant === 'all' ? (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Funnel sammenligning</div>
            <div className="card-subtitle">Step-for-step fra start til lead — Chat vs Beregner</div>
          </div>
          <ComparisonHeader />
          <div className="grid-2 funnel-compare-grid">
            <FunnelChart variant="chat" audience={audienceParam} rows={chatFunnel} activeExcluded={chatActive} resumeLeads={chatResume} loading={loading} />
            <FunnelChart variant="calculator" audience={audienceParam} rows={calcFunnel} activeExcluded={calcActive} resumeLeads={calcResume} loading={loading} />
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <div className="card-title">{variant === 'chat' ? 'Chat funnel' : 'Beregner funnel'}</div>
            <div className="card-subtitle">Step-for-step fra start til lead</div>
          </div>
          <FunnelChart variant={variant} audience={audienceParam} rows={single} activeExcluded={singleActive} resumeLeads={singleResume} loading={loading} />
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="card-title">Hvor forlader brugerne</div>
          <div className="card-subtitle">Sidste step før de forlod sessionen</div>
        </div>
        <DropoffList variant={variantParam} audience={audienceParam} rows={dropoff} loading={loading} />
      </div>
    </>
  )
}
